import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertCircle, Heart, CheckCircle, X } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { SamsungHRSetupGuide } from "./SamsungHRSetupGuide";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";

interface BPMStatusDialogProps {
  open: boolean;
  status: 'idle' | 'scanning' | 'connecting' | 'connected' | 'error' | 'error_samsung' | 'error_bluetooth_disabled' | 'error_no_device' | 'healthkit_available';
  onCancel: () => void;
}

const statusConfig = {
  idle: {
    icon: <Heart className="w-6 h-6 text-muted-foreground" />,
    title: "Aguardando",
    description: "Pronto para conectar ao monitor cardíaco"
  },
  scanning: {
    icon: <Loader2 className="w-6 h-6 animate-spin text-primary" />,
    title: "Procurando Monitor Cardíaco...",
    description: "Certifique-se de que seu monitor está ligado e próximo."
  },
  connecting: {
    icon: <Loader2 className="w-6 h-6 animate-spin text-primary" />,
    title: "Conectando...",
    description: "Estabelecendo conexão com seu monitor cardíaco"
  },
  connected: {
    icon: <CheckCircle className="w-6 h-6 text-success" />,
    title: "✓ Conectado!",
    description: "Monitor cardíaco conectado e funcionando"
  },
  healthkit_available: {
    icon: <CheckCircle className="w-6 h-6 text-success" />,
    title: "✓ Apple Health Disponível",
    description: "Detectamos dados de batimento cardíaco do seu relógio via Apple Health. Usando como fonte."
  },
  error_bluetooth_disabled: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Bluetooth Desativado",
    description: "Por favor, ative o Bluetooth nas configurações do seu dispositivo para conectar monitores cardíacos."
  },
  error_no_device: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Nenhum Monitor Encontrado",
    description: "Certifique-se de que seu monitor cardíaco está ligado, próximo ao celular e pareado via Bluetooth."
  },
  error: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Erro na Conexão",
    description: "Não foi possível conectar ao monitor cardíaco. Tente novamente."
  },
  error_samsung: {
    icon: <AlertCircle className="w-6 h-6 text-destructive" />,
    title: "Monitor Não Encontrado",
    description: "Possível dispositivo Samsung detectado. Veja instruções especiais abaixo."
  }
} as const;

export const BPMStatusDialog = ({ open, status, onCancel }: BPMStatusDialogProps) => {
  const [showSamsungGuide, setShowSamsungGuide] = useState(false);
  const platform = Capacitor.getPlatform();
  const hasHealthKit = platform === 'ios';
  
  // ✅ FIX: Auto-close timer ref for error states
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-close após timeout ou erro
  useEffect(() => {
    if (!open) return;

    // Clear any existing timer
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }

    // Auto-close para 'connected' ou 'healthkit_available' após 2 segundos
    if (status === 'connected' || status === 'healthkit_available') {
      autoCloseTimerRef.current = setTimeout(() => {
        onCancel();
      }, 2000);
    }
    
    // ✅ FIX: Auto-close error dialogs after 30 seconds
    if (status === 'error' || status === 'error_bluetooth_disabled' || status === 'error_no_device') {
      autoCloseTimerRef.current = setTimeout(() => {
        onCancel();
      }, 30000);
    }

    // Show Samsung guide for error_samsung
    if (status === 'error_samsung') {
      setShowSamsungGuide(true);
    }

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open, status, onCancel]);

  const handleUseHealthKit = async () => {
    setShowSamsungGuide(false);
    onCancel();
    
    // Force HealthKit monitoring
    localStorage.setItem('bpm_prefer_healthkit', 'true');
    
    // Notify parent to restart with HealthKit
    window.dispatchEvent(new CustomEvent('bpm-use-healthkit'));
  };
  
  // ✅ FIX: Handle open change - allow closing by clicking outside
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCancel();
    }
  };

  // Não renderizar se não estiver aberto
  if (!open) return null;
  
  // Show Samsung guide instead of normal dialog for error_samsung
  if (status === 'error_samsung') {
    return (
      <SamsungHRSetupGuide
        open={showSamsungGuide}
        onClose={() => {
          setShowSamsungGuide(false);
          onCancel();
        }}
        onUseHealthKit={hasHealthKit ? handleUseHealthKit : undefined}
        hasHealthKit={hasHealthKit}
      />
    );
  }
  
  const config = statusConfig[status];
  
  // Proteção contra status inválido
  if (!config) {
    console.warn(`BPMStatusDialog: Invalid status "${status}"`);
    return null;
  }

  // ✅ FIX: Check if this is an error state for showing auto-close message
  const isErrorState = status === 'error' || status === 'error_bluetooth_disabled' || status === 'error_no_device';

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-[90%] sm:max-w-md">
        {/* ✅ FIX: Add explicit close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="absolute top-3 right-3 h-8 w-8 rounded-full"
        >
          <X className="h-4 w-4" />
        </Button>
        
        <AlertDialogHeader>
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
            {config.icon}
          </div>
          <AlertDialogTitle className="text-center">
            {config.title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {config.description}
          </AlertDialogDescription>
          
          {/* ✅ FIX: Show auto-close message for error states */}
          {isErrorState && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              Fecha automaticamente em 30 segundos
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {isErrorState ? 'Continuar Sem Monitor' : status === 'connected' ? 'OK' : 'Cancelar'}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};